/**
 * AdminLogin - Login page for internal team
 * Supports first-run setup (creates owner account)
 */

import React, { useState } from 'react';
import { setAdminTokens, navigate } from './api';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function AdminLogin() {
  const [mode, setMode] = useState('login'); // login | setup
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupDone, setSetupDone] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Login failed');
        setLoading(false);
        return;
      }

      setAdminTokens(data.token, data.refreshToken, data.admin);
      navigate('/admin/dashboard');
    } catch (err) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/admin/auth/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Setup failed');
        setLoading(false);
        return;
      }

      setSetupDone(true);
      setMode('login');
    } catch (err) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const s = {
    page: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#0A0F1D',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    card: {
      width: 400,
      backgroundColor: '#0F1729',
      borderRadius: 16,
      border: '1px solid #1A2332',
      padding: '40px 36px',
    },
    logo: {
      textAlign: 'center',
      marginBottom: 32,
    },
    logoText: {
      fontSize: 24,
      fontWeight: 800,
      color: '#FFF',
    },
    subtitle: {
      fontSize: 13,
      color: '#5A6175',
      marginTop: 6,
    },
    field: {
      marginBottom: 20,
    },
    label: {
      display: 'block',
      fontSize: 13,
      fontWeight: 600,
      color: '#8B92A5',
      marginBottom: 6,
    },
    input: {
      width: '100%',
      padding: '12px 14px',
      borderRadius: 8,
      border: '1px solid #2A3444',
      backgroundColor: '#1A2332',
      color: '#FFF',
      fontSize: 14,
      outline: 'none',
      boxSizing: 'border-box',
    },
    btn: {
      width: '100%',
      padding: '14px',
      borderRadius: 8,
      border: 'none',
      background: 'linear-gradient(135deg, #0057FF, #0040CC)',
      color: '#FFF',
      fontSize: 15,
      fontWeight: 600,
      cursor: 'pointer',
      marginTop: 8,
    },
    error: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: 8,
      padding: '10px 14px',
      color: '#EF4444',
      fontSize: 13,
      marginBottom: 16,
    },
    success: {
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      borderRadius: 8,
      padding: '10px 14px',
      color: '#10B981',
      fontSize: 13,
      marginBottom: 16,
    },
    toggle: {
      textAlign: 'center',
      marginTop: 20,
      fontSize: 13,
      color: '#5A6175',
    },
    link: {
      color: '#3B82F6',
      cursor: 'pointer',
      textDecoration: 'underline',
    },
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>
          <div style={s.logoText}>
            Talk <span style={{ color: '#0057FF' }}>Australia</span>
          </div>
          <div style={s.subtitle}>
            {mode === 'setup' ? 'First-Time Setup' : 'Admin Portal'}
          </div>
        </div>

        {setupDone && (
          <div style={s.success}>Owner account created. Please log in.</div>
        )}

        {error && <div style={s.error}>{error}</div>}

        <form onSubmit={mode === 'login' ? handleLogin : handleSetup}>
          {mode === 'setup' && (
            <div style={s.field}>
              <label style={s.label}>Full Name</label>
              <input
                style={s.input}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Amit Sewani"
                required
              />
            </div>
          )}

          <div style={s.field}>
            <label style={s.label}>Email</label>
            <input
              style={s.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="amit@talkaustralia.com.au"
              required
            />
          </div>

          <div style={s.field}>
            <label style={s.label}>Password</label>
            <input
              style={s.input}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === 'setup' ? 'Min 10 characters' : 'Enter password'}
              required
            />
          </div>

          <button style={s.btn} type="submit" disabled={loading}>
            {loading ? 'Please wait...' : mode === 'setup' ? 'Create Owner Account' : 'Sign In'}
          </button>
        </form>

        <div style={s.toggle}>
          {mode === 'login' ? (
            <span>
              First time? <span style={s.link} onClick={() => { setMode('setup'); setError(''); }}>Set up owner account</span>
            </span>
          ) : (
            <span>
              Already set up? <span style={s.link} onClick={() => { setMode('login'); setError(''); }}>Sign in</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
